import { SchoolData } from "@nimble/candidate-api/types";
import type { JSX, ReactNode } from "react";

import { buildPrincipalInitials } from "@/app/(discover)/candidate/invites/lib/invites-page.utils";
import { formatPrincipalName } from "@/lib/helpers/format-principal-name";
import { cn } from "@/lib/utils";

export function PrincipalCardAvatar({
  schoolData,
  className,
}: {
  schoolData: SchoolData;
  className?: string;
}): JSX.Element {
  const initials = buildPrincipalInitials(
    schoolData.principal?.first_name,
    schoolData.principal?.last_name,
  );

  return (
    <div
      className={cn([
        "w-10 h-10 rounded-full flex items-center justify-center text-primary-foreground text-base font-semibold bg-foreground",
        className,
      ])}
    >
      {initials}
    </div>
  );
}

function PrincipalCardPrincipalName({
  schoolData,
}: {
  schoolData: SchoolData;
}) {
  return formatPrincipalName(
    schoolData.principal?.first_name,
    schoolData.principal?.last_name,
  );
}

type PrincipalCardContentProps = {
  schoolName?: ReactNode;
  principalName?: ReactNode;
  avatar?: ReactNode;
  extraMessage?: ReactNode;
  extraName?: ReactNode;
};

function PrincipalCardContent({
  schoolName,
  principalName,
  avatar,
  extraMessage,
  extraName,
}: PrincipalCardContentProps): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <div className="relative flex-shrink-0">{avatar}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-semibold text-sm truncate text-foreground">
            {principalName}
          </h4>
          {extraName}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-1">
          {schoolName}
        </p>
        {extraMessage}
      </div>
    </div>
  );
}

export const PrincipalCard = {
  Avatar: PrincipalCardAvatar,
  Content: PrincipalCardContent,
  PrincipalName: PrincipalCardPrincipalName,
};

export function PrincipalCardPreset({
  schoolData,
  ...props
}: {
  schoolData: SchoolData;
} & PrincipalCardContentProps): JSX.Element {
  return (
    <PrincipalCardContent
      avatar={<PrincipalCardAvatar schoolData={schoolData} />}
      schoolName={schoolData.name}
      principalName={<PrincipalCardPrincipalName schoolData={schoolData} />}
      {...props}
    />
  );
}
